import React, { useEffect, useState } from "react";
import { Button, Card, Field, PageHeader, SelectField, TextAreaField } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";
import { api, isDemoMode } from "../api/client";
import { notifyDataChanged } from "../hooks/useCourses";
import { demoCourses } from "../demo/data";
import { useCourses } from "../hooks/useCourses";

export function ClassReportPage() {
  const { t, locale, formatNumber } = useI18n();
  const {courses}=useCourses();
  const [courseId, setCourseId] = useState(demoCourses[0].id);
  useEffect(()=>{if(courses.length&&!courses.some(course=>course.id===courseId))setCourseId(courses[0].id);},[courses,courseId]);
  const [topic, setTopic] = useState("");
  const [summary, setSummary] = useState("");
  const [analyzed, setAnalyzed] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [files,setFiles]=useState<File[]>([]);
  const [error,setError]=useState("");

  const analyze = async () => {
    setLoading(true);setError("");
    try{
      if(isDemoMode()){
        await new Promise((resolve) => setTimeout(resolve, 500));
        if (!summary) setSummary(t("classreport.demoSummary"));
        if (!topic) setTopic(t("classreport.demoTopic"));
      }else{
        // Title and description travel separately — AI must never overwrite a typed title.
        const result=await api<{topic:string;summary:string;suggestedTasks:string[];flashcards:number;quiz:number}>("/class-reports/analyze",{method:"POST",body:JSON.stringify({topic,notes:summary,locale,courseId})});
        setSummary(result.summary);
        if(!topic.trim()) setTopic(result.topic);
      }
      setAnalyzed(true);
    }catch(reason){setError(reason instanceof Error?reason.message:t("common.error"))}
    finally{setLoading(false)}
  };

  const confirm = async () => {
    setLoading(true);setError("");
    try{
      if (!isDemoMode()) {
        // Files FIRST: a mid-file failure then left the report already created,
        // and a retry created a SECOND report + re-uploaded earlier files.
        for (const file of files) {
          const form=new FormData();form.append("file",file);form.append("courseId",courseId);form.append("title",file.name);
          await api("/resources",{method:"POST",body:form});
        }
        const report = await api<{ id: string }>("/class-reports", { method: "POST", body: JSON.stringify({ courseId, topic, summary }) });
        await api(`/class-reports/${report.id}/confirm`, { method: "POST" });
      } else await new Promise((resolve) => setTimeout(resolve, 450));
      setSaved(true);
      notifyDataChanged();
    }catch(reason){setError(reason instanceof Error?reason.message:t("common.error"))}
    finally{setLoading(false)}
  };

  return (
    <div className="page-stack narrow-page">
      <PageHeader title={t("report.title")} subtitle={t("report.subtitle")} />
      <Card className="report-form-card" elevated>
        <div className="report-steps"><span className="is-active">{formatNumber(1)}</span><i /><span className={analyzed ? "is-active" : ""}>{formatNumber(2)}</span><i /><span className={saved ? "is-active" : ""}>{formatNumber(3)}</span></div>
        {saved ? (
          <div className="report-success"><span>✦</span><h2>{t("report.success")}</h2><p>{t("report.successDetail")}</p><Button onClick={() => { setSaved(false); setAnalyzed(false); setTopic(""); setSummary(""); setFiles([]); }}>{t("report.title")}</Button></div>
        ) : (
          <div className="report-form-grid">
            <SelectField label={t("course.title")} value={courseId} onChange={(event) => setCourseId(event.target.value)}>
              {courses.map((course) => <option key={course.id} value={course.id}>{course.title}</option>)}
            </SelectField>
            <Field label={t("report.topic")} value={topic} onChange={(event) => setTopic(event.target.value)} placeholder={t("report.topicPlaceholder")} />
            <TextAreaField label={t("report.summary")} value={summary} onChange={(event) => setSummary(event.target.value)} placeholder={t("report.summaryPlaceholder")} />
            <label className="file-drop"><input type="file" multiple onChange={(event)=>setFiles(Array.from(event.target.files||[]))} /><span>＋</span><strong>{t("report.files")}</strong><small>{files.length?`${t("report.filesSelected", { count: formatNumber(files.length) })}`:t("report.fileTypes")}</small></label>
            {error&&<div className="form-error" role="alert">{error}</div>}
            <div className="report-form-actions">
              <Button variant="secondary" icon="sparkle" loading={loading} onClick={analyze}>{t("report.analyze")}</Button>
              <Button icon="check" disabled={!analyzed || !topic || !summary} loading={loading} onClick={confirm}>{t("report.confirm")}</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
